import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Per-user navigation preferences persisted to localStorage.
 *
 * Stored shape:
 *   {
 *     collapsed: boolean,               // sidebar collapsed to icon rail
 *     expandedGroups: { [key]: bool },  // per-group inline expand state
 *     pinned: string[]                  // array of `to` paths pinned to the top
 *   }
 *
 * First-visit default: collapsed on 1024–1440px screens, expanded on ≥1440px.
 * User's manual toggle always wins after that.
 */

const storageKey = (userId) => `talmia:nav:${userId || "guest"}`;

// Custom in-tab event so every `useNavPrefs()` instance re-reads localStorage
// when ANY consumer writes.  This fixes the long-standing "eye toggle does
// nothing" bug — Shell and Sidebar were each holding their own useState copy.
const NAV_EVT = "talmia-nav-prefs-changed";
function emitChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NAV_EVT));
  }
}

function firstVisitDefault() {
  // Default to collapsed on first visit — the nav is long, users mostly want icons.
  return true;
}

// Users only see actively-built groups by default. Roadmap/planning items are
// hidden until they flip the eye toggle in the sidebar.
function firstVisitHideRoadmap() { return true; }

function readState(userId) {
  if (typeof window === "undefined") {
    return { collapsed: false, expandedGroups: {}, pinned: [], hideRoadmap: firstVisitHideRoadmap() };
  }
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      // v2 migration (Feb 2026) — "hide roadmap items" should be ON by
      // default for everyone. Older stored prefs still carry an explicit
      // `hideRoadmap: false` from previous visits; reset it once on read
      // and stamp `_v: 2` so we only do this once per user.
      if (parsed._v !== 2) {
        parsed.hideRoadmap = firstVisitHideRoadmap();
        parsed._v = 2;
      }
      return {
        collapsed: !!parsed.collapsed,
        expandedGroups: parsed.expandedGroups || {},
        pinned: Array.isArray(parsed.pinned) ? parsed.pinned : [],
        hideRoadmap: parsed.hideRoadmap === undefined ? firstVisitHideRoadmap() : !!parsed.hideRoadmap,
        _v: 2,
      };
    }
  } catch { /* corrupted JSON — fall through */ }
  return { collapsed: firstVisitDefault(), expandedGroups: {}, pinned: [], hideRoadmap: firstVisitHideRoadmap(), _v: 2 };
}

export function useNavPrefs() {
  const { user } = useAuth();
  const userId = user?._id || user?.id || user?.email || "guest";

  const [state, setState] = useState(() => readState(userId));

  // Persist whenever state changes + broadcast so peer instances re-read.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(state));
    } catch { /* storage disabled / quota exceeded — prefs are ephemeral, fine */ }
    emitChange();
  }, [state, userId]);

  // Re-hydrate if the user changes (login/logout swap) OR another consumer
  // in the same tab (or another tab) writes new prefs.  Also honours the
  // browser's native `storage` event for cross-tab sync.
  useEffect(() => {
    const rehydrate = () => {
      const raw = typeof window !== "undefined" && localStorage.getItem(storageKey(userId));
      if (!raw) return;
      // Only setState when content actually changed (identity always differs).
      // Prevents an infinite loop between persist-effect and NAV_EVT listener.
      setState((prev) => {
        const prevStr = JSON.stringify(prev);
        if (prevStr === raw) return prev;
        try { return JSON.parse(raw); } catch { return prev; }
      });
    };
    rehydrate();
    window.addEventListener(NAV_EVT, rehydrate);
    window.addEventListener("storage", rehydrate);
    return () => {
      window.removeEventListener(NAV_EVT, rehydrate);
      window.removeEventListener("storage", rehydrate);
    };
  }, [userId]);

  const toggleCollapsed = useCallback(
    () => setState((s) => ({ ...s, collapsed: !s.collapsed })),
    []
  );

  const toggleHideRoadmap = useCallback(
    // Toggling the eye also auto-collapses every expanded group so the sidebar
    // doesn't remain in a partially-open state after items disappear.
    () => setState((s) => ({ ...s, hideRoadmap: !s.hideRoadmap, expandedGroups: {} })),
    []
  );

  /**
   * Force-close every known group. Consumers pass the current top-level
   * group keys (from the resolved nav) — we set each one to `false` so
   * the "default open" heuristic can't re-open them.
   */
  const collapseAllGroups = useCallback((groupKeys) => {
    setState((s) => {
      const next = { ...s.expandedGroups };
      (groupKeys || []).forEach((k) => { next[k] = false; });
      return { ...s, expandedGroups: next };
    });
  }, []);

  const setGroupExpanded = useCallback((groupKey, expanded) => {
    setState((s) => ({
      ...s,
      expandedGroups: { ...s.expandedGroups, [groupKey]: expanded },
    }));
  }, []);

  const toggleGroup = useCallback((groupKey, currentOpen) => {
    setState((s) => ({
      ...s,
      // When the caller knows the currently-displayed state, trust that.
      // Otherwise fall back to "was it explicitly set to true?".
      expandedGroups: {
        ...s.expandedGroups,
        [groupKey]: typeof currentOpen === "boolean"
          ? !currentOpen
          : !(s.expandedGroups[groupKey] ?? true),
      },
    }));
  }, []);

  const togglePinned = useCallback((to) => {
    setState((s) => {
      const already = s.pinned.includes(to);
      return {
        ...s,
        pinned: already ? s.pinned.filter((p) => p !== to) : [...s.pinned, to],
      };
    });
  }, []);

  const isPinned = useCallback((to) => state.pinned.includes(to), [state.pinned]);

  return {
    collapsed: state.collapsed,
    expandedGroups: state.expandedGroups,
    pinned: state.pinned,
    hideRoadmap: state.hideRoadmap,
    toggleCollapsed,
    toggleHideRoadmap,
    collapseAllGroups,
    setGroupExpanded,
    toggleGroup,
    togglePinned,
    isPinned,
  };
}
